import { NotificationDot } from '@src/components/NotificationBadge/NotificationBadge';
import { ParticipantProgressFilterProvider } from '../ParticipantProgressFilter';
import { ChevronLeftIcon } from '@src/components/icons/ChevronLeftIcon';
import { boxMainPath } from '@src/routing/routes';
import { drawNeedsAttention as needsDrawAttention } from './drawNeedsAttention';
import { Panel } from '@src/components/Panel/Panel';
import { WrenchIcon } from '@src/components/icons/WrenchIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import { OrganizerSettingsTabs } from './OrganizerSettingsTabs';
import { OrganizerDrawTabs } from './OrganizerDrawContent';
import { useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrganizerPanelHome, type OrganizerPanelHomeProps, type OrganizerSort } from './OrganizerPanelHome';
import { OrganizerPanelSettings } from './OrganizerPanelSettings';
import { OrganizerPanelActivity } from '../OrganizerPanelActivity';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { CogIcon } from '@src/components/icons/CogIcon';
import { OrganizerPeoplePanel } from './OrganizerPeoplePanel';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { useMobileBoxLayout } from './BoxLayouts';
import { PaperPlaneIcon } from '@src/components/icons/PaperPlaneIcon';
import './OrganizerPanel.css';

const desktopTabs = ['users', 'invite', 'draw', 'appearance', 'questionnaires', 'actions'] as const;
const mobileTabs = ['users', 'draw', 'settings'] as const;
type Tab = typeof desktopTabs[number] | 'settings';
const icons = { users: UsersIcon, invite: PaperPlaneIcon, draw: GiftIcon, history: ClockIcon, settings: CogIcon, appearance: StarIcon, questionnaires: CogIcon, actions: WrenchIcon };

/** An opt-in management workspace; legacy organizer/settings routes stay available. */
export function OrganizerPanel(props: OrganizerPanelHomeProps) {
  return <ParticipantProgressFilterProvider key={props.info.box.key}><OrganizerPanelContent {...props} /></ParticipantProgressFilterProvider>;
}

function OrganizerPanelContent({ info, cards, onChanged }: OrganizerPanelHomeProps) {
  const navigate = useNavigate();
  const sorting = useState<OrganizerSort[]>([]);
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const mobile = useMobileBoxLayout();
  const drawNeedsAttention = needsDrawAttention(info, cards);
  const tabs: readonly Tab[] = mobile ? mobileTabs : desktopTabs;
  const requested = params.get('tab') === 'more' ? 'history' : params.get('tab') ?? ({ '#draw': 'draw', '#history': 'history', '#organizers': 'organizers' }[location.hash]);
  const selected = (!requested || requested === 'users') ? params.get('people') ?? 'users' : requested;
  const settingsSections = ['appearance', 'questionnaires', 'actions'] as const;
  const settingsSection = settingsSections.find(tab => tab === (selected === 'settings' ? params.get('settingsTab') : selected)) ?? 'questionnaires';
  const normalized = selected === 'history' ? 'users' : mobile && (selected === 'invite' || selected === 'organizers' || selected === 'history') ? 'users'
    : mobile && settingsSections.some(tab => tab === selected) ? 'settings'
    : !mobile && selected === 'settings' ? settingsSection
    : selected === 'organizers' ? 'invite' : selected;
  const active: Tab = tabs.find(tab => tab === normalized) ?? 'users';

  function select(tab: Tab) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (!mobile || tab === 'users') next.delete('people');
      if (settingsSections.some(section => section === tab)) next.set('settingsTab', tab);
      next.set('tab', tab);
      return next;
    }, { preventScrollReset: true });
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    buttons.current[next]?.focus();
    select(tabs[next]);
  }

  return <div className={`organizer-panel${active === 'settings' || (mobile && (active === 'users' || active === 'draw')) ? ' organizer-panel--nested-nav' : ''}`} data-testid="organizer-panel">
    <div className="organizer-panel__tabs" role="tablist" aria-label={t('organizerPanel.label')}>
      {mobile && <button type="button" className="organizer-panel__back" aria-label={t('organizerPanel.backToGame')} title={t('organizerPanel.backToGame')} data-testid="organizer-back-to-game" onClick={() => navigate(boxMainPath(info.box.key))}><ChevronLeftIcon /></button>}
      {tabs.map((tab, index) => { const Icon = icons[tab]; return <button
        key={tab}
        ref={node => { buttons.current[index] = node; }}
        type="button"
        role="tab"
        id={`organizer-tab-${tab}`}
        aria-controls={`organizer-panel-${tab}`}
        aria-selected={active === tab}
        aria-label={tab === 'draw' && drawNeedsAttention ? `${t('organizerPanel.draw')}: ${t('game.draw.needsAttention')}` : t(`organizerPanel.${tab}`)}
        title={t(`organizerPanel.${tab}`)}
        tabIndex={active === tab ? 0 : -1}
        onClick={() => select(tab)}
        onKeyDown={event => onKeyDown(event, index)}
        data-testid={`organizer-tab-${tab}`}
      ><span className="organizer-panel__tab-icon"><Icon />{tab === 'draw' && drawNeedsAttention && <NotificationDot className="organizer-panel__attention-dot" testId="organizer-draw-attention-dot" />}</span><span className="organizer-panel__tab-label">{t(`organizerPanel.${tab}Short`, { defaultValue: t(`organizerPanel.${tab}`) })}</span></button>; })}
    </div>
    <section key={active} role="tabpanel" id={`organizer-panel-${active}`}
      aria-labelledby={`organizer-tab-${active}`} tabIndex={0}>
      {active === 'settings' ? <div className="organizer-people--nested">
        <OrganizerSettingsTabs includeAppearance={mobile} />
        <div role="tabpanel" id={`settings-content-${settingsSection}`} aria-labelledby={`settings-subtab-${settingsSection}`}>
          <OrganizerPanelSettings key={settingsSection} info={info} onChanged={onChanged} showHeader={false} section={settingsSection} />
        </div>
      </div> : mobile && active === 'draw' ? <div className="organizer-people--nested">
        <OrganizerDrawTabs />
        <OrganizerPanelHome sorting={sorting} info={info} cards={cards} onChanged={onChanged} section="draw" />
      </div> : active === 'invite' || (!mobile && active === 'users')
        ? <>{active === 'users' && <OrganizerPanelActivity info={info} cards={cards} />}<Panel padding="none" elevated className="organizer-people__content"><OrganizerPanelHome sorting={sorting} info={info} cards={cards} onChanged={onChanged} embedded section={active} /></Panel></>
        : active === 'users'
        ? <OrganizerPeoplePanel sorting={sorting} info={info} cards={cards} onChanged={onChanged} />
        : active === 'appearance' || active === 'questionnaires' || active === 'actions'
        ? <OrganizerPanelSettings key={active} info={info} onChanged={onChanged} showHeader={false} section={active} />

        : <OrganizerPanelHome sorting={sorting} info={info} cards={cards} onChanged={onChanged} section={active} />}
    </section>
  </div>;
}
